import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Form, Input, Modal, Select } from 'antd';

import { InputIconSelect } from '../Input';
import ToggleRow from '../common/ToggleRow';
import { CardOptionLabel } from '../cards/CardVisuals';
import TagBadge from './TagBadge';
import TagRefusal from './TagRefusal';
import { confirmNewItems } from './confirmNewItems';
import {
	TAG_LIMITS,
	TAG_SEPARATOR,
	applyToNewItemsOf,
	canAddUnder,
	cleanAliases,
	describeNewItemsWarning,
	describeTagInUse,
	hasChildren,
	isRestrictedTag,
	tagIdOf,
	tagParentIdOf,
	tagRefusal,
	treeOrder,
} from '../../utils/tags';
import { isAutomaticTag } from '../../utils/tagRules';

/**
 * Add tag · Edit tag (docs/plans/tags.md §5.2 Settings › Tags, Account › Tags):
 * the name, the icon and color (one picker: the tile every chip of the tag
 * wears), a description, and — for a collection tag — Restricted. A new tag
 * also says where it goes (**Under**); an existing one moves with Move to….
 *
 * Only what changed is sent (the API judges only what changes, and a field
 * sent back as stored is an echo).
 *
 * ALIASES (T9): other names the tag answers to — in filters, search and what
 * an assistant types. One antd tags Select: type a name and press Enter. The
 * API's rules, in its words (utils/tags.js cleanAliases): at most 10, no "/",
 * each an ordinary tag name; a duplicate, or the tag's own name, is left out
 * quietly. A name that breaks a rule is not added, and the field says why.
 *
 * RESTRICTED (plan §3.5, ruled d09: fully locked). A restricted tag is put on
 * and taken off by managers only, and stands alone — no tag above it, none
 * under it. The switch says why when it cannot move: under a tag, or holding
 * tags. An access setting that names it holds it too; that the API says
 * (`tag_in_use`), in its own words, on Save. An AUTOMATIC tag (T6a) can't be
 * restricted — a restricted tag is put on by managers, by hand — so the switch
 * is closed on a tag a rule applies, with the reason (the rule's own ⋯ action
 * is closed on a restricted tag, the other way round).
 *
 * APPLY TO NEW ITEMS OF (T7, ruled d22; `newItemCards`, collection tags): on
 * a restricted tag's Edit…, the cards whose new items get the tag as they are
 * made — anyone's, in the collection or a sub-collection — so an access level
 * that leaves the tag out never shows them, even for the moment before a
 * manager tags them. An antd multiple Select of the collection's cards and
 * its sub-collections' (names shown, ids stored), at most 20. It is sent only
 * while the tag stays restricted (the API clears it when a tag is
 * un-restricted, and judges it against the restriction the save ends with);
 * ⚑ a new tag gets it from Edit…, once it exists (the contract sets it with
 * PATCH). A refusal about it (`invalid_tag`, `field: 'applyToNewItemsOf'`) is
 * said under it; a restricted tag that access settings name says which,
 * each with its link (components/tags/TagRefusal.js).
 *
 * WIDENING IT WARNS FIRST (keepr-api b75): a save that ADDS a card to the
 * list asks the change's dry run (`onCheck`) before it saves. When access
 * settings show items with the tag — they will show every new item of the
 * added cards — the person is asked (confirmNewItems.js: each setting they
 * manage named with its link, the rest counted); **Save tag** saves, **Keep
 * editing** leaves the dialog open with nothing saved. Narrowing or clearing
 * the list asks nothing. A refusal the dry run meets is the save's, said
 * here as the save's would be.
 *
 * `onSave(fields, { tag })` performs the write and resolves with the saved tag;
 * a refusal it throws is shown here, in the dialog, and the dialog stays open.
 * `onCheck(fields, { tag })` → `{ warnings }` (the dry run); without it the
 * save goes straight ahead.
 */
// What "Apply to new items of" holds now: only a restricted tag has a list
// (un-restricting clears it).
const storedNewItems = tag => (isRestrictedTag(tag) ? applyToNewItemsOf(tag) : []);

export default function TagEditModal({
	open,
	onClose,
	onSave,
	onCheck = null,
	tag = null,
	tags = [],
	parentId: presetParentId = null,
	allowRestricted = false,
	privateTags = false,
	newItemCards = null,
}) {
	const editing = Boolean(tag);
	const { modal } = App.useApp();
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [icon, setIcon] = useState(null);
	const [color, setColor] = useState(null);
	const [parentId, setParentId] = useState(null);
	const [restricted, setRestricted] = useState(false);
	const [aliases, setAliases] = useState([]);
	const [aliasError, setAliasError] = useState(null);
	const [newItemsOf, setNewItemsOf] = useState([]);
	const [newItemsError, setNewItemsError] = useState(null);
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState(null);
	const [nameError, setNameError] = useState(null);

	useEffect(() => {
		if (!open) return;
		setName(tag?.name || '');
		setDescription(tag?.description || '');
		setIcon(tag?.icon || null);
		setColor(tag?.color || null);
		setParentId(editing ? tagParentIdOf(tag) : (presetParentId || null));
		setRestricted(Boolean(tag?.restricted));
		setAliases(Array.isArray(tag?.aliases) ? tag.aliases : []);
		setAliasError(null);
		setNewItemsOf(storedNewItems(tag));
		setNewItemsError(null);
		setBusy(false);
		setRefusal(null);
		setNameError(null);
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	const ordered = useMemo(() => treeOrder(tags), [tags]);
	// Where a NEW tag may go: the top level, or under a tag with room beneath
	// it that is not restricted.
	const parentOptions = useMemo(() => [
		{ value: '', label: 'Top level', searchText: 'Top level' },
		...ordered.map(candidate => {
			const verdict = canAddUnder(tags, tagIdOf(candidate));
			return {
				value: tagIdOf(candidate),
				disabled: !verdict.ok,
				title: verdict.ok ? undefined : verdict.reason,
				searchText: (candidate.path || [candidate.name]).join(` ${TAG_SEPARATOR} `),
				label: (
					<span className="tag-edit__parent-option" style={{ '--tag-depth': candidate.depth || 1 }}>
						<TagBadge tag={candidate} size="xs" />
					</span>
				),
			};
		}),
	], [ordered, tags]);

	// Restricted stands alone: say why it cannot be switched on.
	const restrictBlocked = (() => {
		if (restricted) return null;
		if (editing && isAutomaticTag(tag)) return 'A rule applies this tag, so it can’t be restricted. Stop applying it by rule first.';
		if (parentId) return 'A restricted tag stands alone: move it to the top level first.';
		if (editing && hasChildren(tags, tagIdOf(tag))) return 'A restricted tag stands alone: move the tags under it first.';
		return null;
	})();

	const trimmed = name.replace(/\s+/g, ' ').trim();
	const localNameError = (() => {
		if (!trimmed) return null;
		if (trimmed.normalize('NFKC').includes(TAG_SEPARATOR)) return 'A tag name cannot contain "/" — it separates a tag from the tag above it.';
		if ([...trimmed].length > TAG_LIMITS.name) return `A tag name is at most ${TAG_LIMITS.name} characters.`;
		return null;
	})();

	// A name typed as an alias is not added when it breaks a rule; the field
	// says which. A duplicate, or the tag's own name, drops out quietly.
	const changeAliases = next => {
		const { aliases: cleaned, error } = cleanAliases(next, trimmed);
		if (error) {
			setAliasError(error);
			return;
		}
		setAliasError(null);
		setAliases(cleaned);
	};

	// "Apply to new items of" is offered on an existing collection tag while
	// its Restricted switch is on (T7).
	const offersNewItems = Boolean(editing && allowRestricted && Array.isArray(newItemCards) && restricted);
	const newItemOptions = useMemo(() => {
		const offered = (newItemCards || []).map(option => ({
			value: option.value,
			label: option.card?.name || 'Untitled card',
			searchText: option.searchText || option.card?.name || '',
			card: option.card,
			depth: option.depth || 0,
		}));
		// A card the list no longer holds (removed from the collection since)
		// still shows, so it can be taken off.
		const known = new Set(offered.map(option => option.value));
		newItemsOf.filter(id => !known.has(id)).forEach(id => offered.push({ value: id, label: 'A card no longer in this collection', searchText: '', card: null, depth: 0 }));
		return offered;
	}, [newItemCards, newItemsOf]);

	const changes = () => {
		const out = {};
		if (!editing || trimmed !== tag.name) out.name = trimmed;
		// The tag's own name, if it was renamed into one, drops out (the API does the same).
		const keptAliases = cleanAliases(aliases, trimmed).aliases;
		if (!editing ? keptAliases.length > 0 : JSON.stringify(keptAliases) !== JSON.stringify(tag.aliases || [])) out.aliases = keptAliases;
		const desc = description.trim() || null;
		if (!editing ? desc !== null : desc !== (tag.description || null)) out.description = desc;
		if (!editing ? icon : (icon || null) !== (tag.icon || null)) out.icon = icon || null;
		if (!editing ? color : (color || null) !== (tag.color || null)) out.color = color || null;
		if (!editing && parentId) out.parentId = parentId;
		if (allowRestricted && (editing ? restricted !== Boolean(tag.restricted) : restricted)) out.restricted = restricted;
		if (offersNewItems) {
			const stored = storedNewItems(tag);
			if (JSON.stringify([...newItemsOf].sort()) !== JSON.stringify([...stored].sort())) out.applyToNewItemsOf = newItemsOf;
		}
		return out;
	};

	// b75: does this save ADD a card to "Apply to new items of"?
	const widensNewItems = fields => Array.isArray(fields.applyToNewItemsOf)
		&& fields.applyToNewItemsOf.some(id => !storedNewItems(tag).includes(id));

	// The added cards by name, as the dialog's own options call them.
	const cardNamesOf = ids => ids
		.map(id => newItemOptions.find(option => option.value === id))
		.filter(option => option && option.card)
		.map(option => option.label);

	const submit = async () => {
		if (!trimmed) { setNameError('Give the tag a name.'); return; }
		if (localNameError) { setNameError(localNameError); return; }
		const fields = changes();
		if (editing && !Object.keys(fields).length) { onClose(); return; }
		setBusy(true);
		setRefusal(null);
		try {
			if (editing && typeof onCheck === 'function' && widensNewItems(fields)) {
				const check = await onCheck(fields, { tag });
				const parts = describeNewItemsWarning(check?.warnings);
				if (parts) {
					const cardIds = parts.cardIds.length ? parts.cardIds : fields.applyToNewItemsOf.filter(id => !storedNewItems(tag).includes(id));
					const names = cardNamesOf(cardIds);
					const go = await confirmNewItems(modal, { tagName: trimmed, parts, cardNames: names.length === cardIds.length ? names : [] });
					if (!go) return;
				}
			}
			await onSave(fields, { tag });
			onClose();
		} catch (error) {
			const data = error?.response?.data || {};
			const text = tagRefusal(error, undefined, { tagName: tag?.name || null });
			if (data.code === 'tag_name_taken' || (data.code === 'invalid_tag' && data.field === 'name')) setNameError(text);
			else if (data.code === 'invalid_tag' && data.field === 'aliases') setAliasError(text);
			else if (data.code === 'invalid_tag' && data.field === 'applyToNewItemsOf' && offersNewItems) setNewItemsError(text);
			else if (describeTagInUse(error)) setRefusal(<TagRefusal error={error} tagName={tag?.name || null} />);
			else setRefusal(text);
		} finally {
			setBusy(false);
		}
	};

	const title = editing ? `Edit “${tag.name}”` : 'Add tag';

	return (
		<Modal
			open={open}
			title={title}
			okText={editing ? 'Save tag' : 'Add tag'}
			onOk={submit}
			okButtonProps={{ loading: busy, disabled: !trimmed }}
			onCancel={onClose}
			destroyOnHidden
			className="tag-edit-modal"
		>
			{refusal ? <Alert type="error" showIcon className="tag-manager__refusal" message={refusal} /> : null}
			<Form layout="vertical" className="tag-edit-modal__form">
				<Form.Item
					label="Name"
					required
					validateStatus={nameError || localNameError ? 'error' : undefined}
					help={nameError || localNameError || undefined}
				>
					<Input
						autoFocus
						maxLength={TAG_LIMITS.name}
						value={name}
						placeholder="e.g. Urgent"
						aria-label="Name"
						onChange={event => { setName(event.target.value); setNameError(null); }}
						onPressEnter={submit}
					/>
				</Form.Item>
				<InputIconSelect
					elementName="icon"
					elementLabel="Icon and color"
					value={icon}
					onChange={(_, value) => setIcon(value)}
					colorElementName="color"
					colorValue={color}
					onColorChange={(_, hex) => setColor(hex)}
					recordKind="tag"
				/>
				<Form.Item
					label="Aliases"
					validateStatus={aliasError ? 'error' : undefined}
					help={aliasError || undefined}
					extra={`Other names that find this tag in filters and search. Type a name and press Enter. ${aliases.length} of ${TAG_LIMITS.aliases}.`}
				>
					<Select
						mode="tags"
						value={aliases}
						onChange={changeAliases}
						open={false}
						suffixIcon={null}
						maxCount={TAG_LIMITS.aliases}
						placeholder="e.g. Tummy ache"
						aria-label="Aliases"
						className="tag-edit-modal__aliases"
					/>
				</Form.Item>
				<Form.Item label="Description" extra="What the tag means, for whoever puts it on an item.">
					<Input.TextArea
						value={description}
						maxLength={TAG_LIMITS.description}
						autoSize={{ minRows: 2, maxRows: 5 }}
						aria-label="Description"
						onChange={event => setDescription(event.target.value)}
					/>
				</Form.Item>
				{!editing ? (
					<Form.Item label="Under" extra="Put it under another tag to group them. Tags nest three levels deep at most.">
						<Select
							value={parentId || ''}
							onChange={value => { setParentId(value || null); if (value) setRestricted(false); }}
							options={parentOptions}
							showSearch
							optionFilterProp="searchText"
							aria-label="Under"
							popupMatchSelectWidth={false}
						/>
					</Form.Item>
				) : null}
				{allowRestricted ? (
					<ToggleRow
						first
						id="tag-edit-restricted"
						label="Restricted"
						description={restrictBlocked
							|| 'Only managers can put it on items or take it off. It stands alone: no tag above it or under it.'}
						checked={restricted}
						disabled={Boolean(restrictBlocked)}
						onChange={value => setRestricted(Boolean(value))}
					/>
				) : null}
				{offersNewItems ? (
					<Form.Item
						label="Apply to new items of"
						className="tag-edit-modal__new-items"
						validateStatus={newItemsError ? 'error' : undefined}
						help={newItemsError || undefined}
						extra={`New items of these cards get this tag as they’re made, so an access level that leaves it out never shows them. ${newItemsOf.length} of ${TAG_LIMITS.applyToNewItemsOf}.`}
					>
						<Select
							mode="multiple"
							value={newItemsOf}
							onChange={next => { setNewItemsOf(Array.isArray(next) ? next : []); setNewItemsError(null); }}
							options={newItemOptions}
							optionRender={option => (option.data.card
								? <CardOptionLabel card={option.data.card} depth={option.data.depth} />
								: option.data.label)}
							optionFilterProp="searchText"
							maxCount={TAG_LIMITS.applyToNewItemsOf}
							placeholder={newItemOptions.length ? 'Choose cards' : 'This collection has no cards yet'}
							notFoundContent="No cards match"
							aria-label="Apply to new items of"
							popupMatchSelectWidth={false}
						/>
					</Form.Item>
				) : null}
				{privateTags ? (
					<p className="meta-line tag-edit-modal__private">Only you see this.</p>
				) : null}
			</Form>
		</Modal>
	);
}
